// Spec 44: the words and numbers the Runs page and the run builder share.
//
// Stop times are the zoneless wall-clock values the load forms exchange
// (lib/stopTime.js), read the same way StopsEditor reads them, so a run's
// stop and the same stop on its load page never print two different times.
//
// Every distance here is one the API already computed (RunLegs /
// RunAllocation). This module only labels them; an estimate is always
// called one.

import moment from 'moment';

/** How long the builder waits after a move before asking POST /runs/:id/check. */
export const CHECK_DEBOUNCE_MS = 400;

const WINDOW_FORMAT = 'ddd MMM D, h:mm A';

/** "Pickup" / "Drop" for a stop's `type` ('load' | 'drop'). */
export function stopTypeLabel(type) {
  const t = String(type || '').toLowerCase();
  if (t === 'load' || t === 'pickup') return 'Pickup';
  if (t === 'drop' || t === 'delivery') return 'Drop';
  return type ? String(type) : 'Stop';
}

/** "Wed Nov 4, 8:00 AM", or an em dash when there is no usable time. */
export function timeLabel(value) {
  if (!value) return '—';
  const m = moment(value);
  return m.isValid() ? m.format(WINDOW_FORMAT) : '—';
}

/** The stop's appointment as one sentence, the way StopsEditor says it. */
export function windowLabel(stop) {
  const start = stop && stop.time ? moment(stop.time) : null;
  const end = stop && stop.appointment_end ? moment(stop.appointment_end) : null;
  if (!start || !start.isValid()) return 'No time set';
  const from = start.format(WINDOW_FORMAT);
  if (end && end.isValid() && end.isAfter(start)) {
    const to = end.isSame(start, 'day') ? end.format('h:mm A') : end.format(WINDOW_FORMAT);
    return `Window ${from} – ${to}`;
  }
  if (stop.appointment_type === 'fcfs') return `First come, first served from ${from}`;
  return `Appointment ${from}`;
}

function num(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** "123.4", or an em dash for a figure the API did not have. */
export function milesLabel(value) {
  const n = num(value);
  if (n === null) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

/** "12,500 lb", or null. */
export function weightLabel(lb) {
  const n = num(lb);
  if (n === null) return null;
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 0 })} lb`;
}

/** Where a load's weight or pallet count was read from. */
export function sourceLabel(source) {
  if (source === 'bol') return 'from BOL';
  if (source === 'freight') return 'from freight';
  return null;
}

/** The basis a leg was split on (RunAllocation.basisFor). */
export function basisLabel(basis) {
  if (basis === 'weight') return 'split by weight';
  if (basis === 'pallets') return 'split by pallets';
  if (basis === 'equal') return 'split equally';
  return null;
}

/** The tenant's runs.shared_mile_split, named. */
export function splitSettingLabel(split) {
  if (split === 'pallets') return 'Shared miles split by pallets';
  if (split === 'equal') return 'Shared miles split equally';
  return 'Shared miles split by weight';
}

/** The tenant's runs.empty_miles, named. */
export function emptySettingLabel(empty) {
  return empty === 'separate' ? 'Empty miles kept on the run' : 'Empty miles spread across loads';
}

/** "Jamie Rivera · Truck T-100 · Trailer TR-200" (whatever the run has). */
export function equipmentLine(run) {
  if (!run) return '';
  const parts = [];
  parts.push(run.driver && run.driver.name ? run.driver.name : 'No driver');
  if (run.truck) parts.push(`Truck ${run.truck.unit_number}`);
  if (run.trailer) parts.push(`Trailer ${run.trailer.unit_number}`);
  return parts.join(' · ');
}

/** A load's short name: the API's label, else its PO / reference / id. */
export function loadLabel(load) {
  if (!load) return '';
  if (load.label) return load.label;
  const ref = load.po || load.customer_reference;
  return ref ? `load ${ref}` : `load ${load.id}`;
}

/**
 * The messages of a refusal. A 400 REQUEST_VALIDATION_ERROR carries
 * `errors: {field: [message]}`; anything else falls back to its title.
 */
export function refusalMessages(response, fallback) {
  const data = response && response.data;
  if (data && data.errors && typeof data.errors === 'object') {
    const messages = Object.values(data.errors)
      .flat()
      .filter(m => typeof m === 'string' && m);
    if (messages.length) return messages;
  }
  if (data && data.title) return [data.title];
  return [fallback];
}
